import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Lock as LockIcon,
  Public as PublicIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { leadNotesAPI } from '../../services/api';

const LeadNotes = ({ jobId, leadId }) => {
  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Personal, 2: Shared
  const [noteText, setNoteText] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editIsShared, setEditIsShared] = useState(false);

  const queryClient = useQueryClient();

  // Fetch notes based on active tab
  const { data: notes, isLoading, error } = useQuery(
    ['leadNotes', jobId, leadId, activeTab],
    () => {
      if (activeTab === 1) return leadNotesAPI.getPersonalNotes(jobId, leadId);
      if (activeTab === 2) return leadNotesAPI.getSharedNotes(jobId, leadId);
      return leadNotesAPI.getNotes(jobId, leadId);
    }
  );

  const createNoteMutation = useMutation(
    (noteData) => leadNotesAPI.createNote(jobId, leadId, noteData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['leadNotes', jobId, leadId]);
        queryClient.invalidateQueries(['jobLeads', String(jobId)]);
        setNoteText('');
        setIsShared(false);
      },
    }
  );

  const updateNoteMutation = useMutation(
    ({ noteId, noteData }) => leadNotesAPI.updateNote(noteId, noteData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['leadNotes', jobId, leadId]);
        queryClient.invalidateQueries(['jobLeads', String(jobId)]);
        setEditingNoteId(null);
        setEditText('');
      },
    }
  );

  const deleteNoteMutation = useMutation(
    (noteId) => leadNotesAPI.deleteNote(noteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['leadNotes', jobId, leadId]);
        queryClient.invalidateQueries(['jobLeads', String(jobId)]);
      },
    }
  );

  const handleAddNote = () => {
    if (!noteText.trim()) {
      alert('Please enter note text');
      return;
    }

    createNoteMutation.mutate({ note_text: noteText, is_shared: isShared });
  };

  const handleStartEdit = (note) => {
    setEditingNoteId(note.id);
    setEditText(note.note_text);
    setEditIsShared(note.is_shared);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditText('');
  };

  const handleSaveEdit = () => {
    if (!editText.trim()) {
      alert('Note text cannot be empty');
      return;
    }

    updateNoteMutation.mutate({
      noteId: editingNoteId,
      noteData: { note_text: editText, is_shared: editIsShared },
    });
  };

  const handleDelete = (noteId) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteNoteMutation.mutate(noteId);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box>
      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
        <Tab label="All Notes" />
        <Tab label="Personal" />
        <Tab label="Shared" />
      </Tabs>

      <Box sx={{ mt: 2 }}>
        {/* Add Note Section */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Add New Note
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Enter your note here..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isShared}
                    onChange={(e) => setIsShared(e.target.checked)}
                  />
                }
                label="Share this note with team"
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNote}
                disabled={createNoteMutation.isLoading}
              >
                Add Note
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Notes List */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load notes
          </Alert>
        )}

        {notes && notes.length === 0 && (
          <Alert severity="info">
            No notes yet. Add your first note above!
          </Alert>
        )}

        {notes && notes.map((note) => (
          <Card key={note.id} sx={{ mb: 2 }}>
            <CardContent>
              {editingNoteId === note.id ? (
                // Edit Mode
                <>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editIsShared}
                          onChange={(e) => setEditIsShared(e.target.checked)}
                        />
                      }
                      label="Share this note"
                    />
                    <Box>
                      <IconButton onClick={handleCancelEdit} size="small">
                        <CancelIcon />
                      </IconButton>
                      <IconButton onClick={handleSaveEdit} size="small" color="primary">
                        <SaveIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </>
              ) : (
                // View Mode
                <>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {note.is_shared ? (
                        <PublicIcon fontSize="small" color="action" />
                      ) : (
                        <LockIcon fontSize="small" color="action" />
                      )}
                      <Typography variant="caption" color="textSecondary">
                        {note.user_full_name || note.user_email}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        • {formatDate(note.created_at)}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton onClick={() => handleStartEdit(note)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(note.id)} size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {note.note_text}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default LeadNotes;

